(function () {
  const queryOne = (selector, root = document) => root.querySelector(selector);
  const queryAll = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  const isVariantAvailable = (variant) =>
    variant &&
    (variant.available == null ||
      variant.available === true ||
      variant.available === 'true' ||
      variant.available === 1);

  const OPTION_KEYS = ['option1', 'option2', 'option3', 'option4', 'option5'];

  const hasVariantKey = (variants, key) =>
    Array.isArray(variants) &&
    variants.some((v) => v && String(v[key] || '').trim() !== '');

  const getRequiredOptionKeys = (variants) =>
    OPTION_KEYS.filter((k) => hasVariantKey(variants, k));

  const variantsCache = new WeakMap();
  const getVariants = (picker) => {
    const raw = picker?.dataset?.variants || '[]';
    const cached = variantsCache.get(picker);
    if (cached && cached.raw === raw) return cached.data;
    let data = [];
    try {
      data = JSON.parse(raw) || [];
    } catch {}
    variantsCache.set(picker, { raw, data });
    return data;
  };

  const getSelectedValue = (picker, inputName) => {
    const radio = picker.querySelector(`input[name="${inputName}"]:checked`);
    if (radio) return radio.value;
    const select = picker.querySelector(`select[name="${inputName}"]`);
    if (select) return select.value;
    const input = picker.querySelector(`input[name="${inputName}"]`);
    if (input && input.type !== 'radio') return input.value || null;
    return null;
  };

  const findMatchingVariant = (variants, selectedByKey) => {
    const requiredKeys = getRequiredOptionKeys(variants);
    if (!requiredKeys.every((k) => selectedByKey[k])) return null;

    return (
      variants.find(
        (v) =>
          v &&
          requiredKeys.every(
            (k) =>
              String(v[k] ?? '').trim().toLowerCase() ===
              String(selectedByKey[k] ?? '').trim().toLowerCase()
          ) &&
          isVariantAvailable(v)
      ) || null
    );
  };

  const toCents = (price) => {
    if (price == null) return 0;
    if (typeof price === 'number') return Math.round(price);
    if (typeof price === 'string') {
      const trimmed = price.trim();
      if (/^\d+$/.test(trimmed)) return Number(trimmed);
      let normalized = trimmed.replace(/[^\d.,-]/g, '').replace(/\s/g, '');
      if (normalized.includes('.') && normalized.includes(',')) {
        normalized = normalized.replace(/\./g, '').replace(',', '.');
      } else if (normalized.includes(',')) {
        normalized = normalized.replace(',', '.');
      }
      const floatValue = parseFloat(normalized);
      return Number.isFinite(floatValue) ? Math.round(floatValue * 100) : 0;
    }
    if (typeof price === 'object') return toCents(price.amount ?? price.price);
    return 0;
  };

  const getMaxPriceForPicker = (variants) => {
    if (!Array.isArray(variants) || variants.length === 0) return 0;
    let max = 0;
    for (const v of variants) {
      if (!isVariantAvailable(v)) continue;
      const p = toCents(v?.price);
      if (p > max) max = p;
    }
    return max;
  };

  const getCompareAtCents = (v) => {
    const p = toCents(v?.price);
    const c = toCents(v?.compare_at_price);
    return c > p ? c : p;
  };
  
  const getMaxCompareForPicker = (variants) => {
    if (!Array.isArray(variants) || variants.length === 0) return 0;
    let max = 0;
    for (const v of variants) {
      if (!isVariantAvailable(v)) continue;
      const c = getCompareAtCents(v);
      if (c > max) max = c;
    }
    return max;
  };

  // ===== Main product variant helpers (base price from selected product options) =====
  let mainVariantsMemo = null;
  function getMainProductVariants() {
    if (mainVariantsMemo) return mainVariantsMemo;
    let variants = [];
    try {
      const jsonEl = document.querySelector('variant-selects script[type="application/json"]');
      if (jsonEl && jsonEl.textContent) {
        variants = JSON.parse(jsonEl.textContent) || [];
      }
    } catch {}
    mainVariantsMemo = Array.isArray(variants) ? variants : [];
    return mainVariantsMemo;
  }

  function getMainVariantIdFromPage() {
    const form =
      document.querySelector('form[action*="/cart/add"]') ||
      document.querySelector('form[action="/cart/add"]') ||
      document.querySelector('form[action$="/cart/add"]');
    const idField =
      form?.querySelector('input[name="id"]') ||
      document.querySelector('input[name="id"]');
    const val = idField && idField.value ? Number(idField.value) : null;
    return Number.isFinite(val) && val > 0 ? val : null;
  }

  function getSelectedMainOptions() {
    const selectedByKey = {};
    const container =
      document.querySelector('variant-selects') || document;
    const fieldsets = Array.from(
      container.querySelectorAll('fieldset[data-index^="option"]')
    );
    for (const fs of fieldsets) {
      const idxAttr = fs.getAttribute('data-index'); // e.g., "option1"
      const m = idxAttr && idxAttr.match(/^option(\d+)/);
      if (!m) continue;
      const n = m[1];
      const checked =
        fs.querySelector('input[type="radio"]:checked') ||
        fs.querySelector('select');
      if (checked && checked.value) {
        selectedByKey[`option${n}`] = String(checked.value).trim();
      }
    }
    return selectedByKey;
  }

  function getCurrentMainVariant() {
    const mainVariants = getMainProductVariants();
    if (!Array.isArray(mainVariants) || mainVariants.length === 0) return null;
    const byId = getMainVariantIdFromPage();
    if (byId) {
      const viaId = mainVariants.find((v) => v && Number(v.id) === byId);
      if (viaId) return viaId;
    }
    const selectedByKey = getSelectedMainOptions();
    const viaOptions = findMatchingVariant(mainVariants, selectedByKey);
    return viaOptions || null;
  }

  const formatMoney = (centsValue) => {
    const value = Math.round(Number(centsValue) || 0);

    const currency =
      (window.Shopify && Shopify.currency && Shopify.currency.active) ||
      document.documentElement.getAttribute('data-shopify-currency') ||
      'DKK';

    if (currency === 'DKK' || currency === 'NOK') {
      return new Intl.NumberFormat('da-DK', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value / 100);
    }

    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      const moneyFormat =
        window.Shopify.money_format ||
        window.theme?.moneyFormat ||
        window.theme?.money_format;
      if (moneyFormat) return Shopify.formatMoney(value, moneyFormat);
    }

    try {
      return new Intl.NumberFormat(navigator.language || 'en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value / 100);
    } catch {
      return (value / 100).toFixed(2);
    }
  };

  function newGroupId() {
    if (window.crypto && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    const t = Date.now().toString(36);
    const r = Math.random().toString(36).slice(2, 10);
    return `g_${t}_${r}`;
  }

  function computePickersStateAndApplyHiddenInputs() {
    let totalCentsFromAddons = 0;
    let totalCompareCentsFromAddons = 0;
    let allPickersComplete = true;
    let maxPotentialAddons = 0;
    let maxPotentialCompareAddons = 0;   
  
    const pickers = queryAll('.variant-picker');
    for (const picker of pickers) {
      const pickerIndex = picker.dataset.idx;
      const addonToggle = picker.querySelector(`#addon-toggle-${pickerIndex}`);
      const isEnabled = !addonToggle || addonToggle.checked;
  
      const idInput = picker.querySelector(`#addon-${pickerIndex}-id`);
      const attrInput = picker.querySelector(`#addon-${pickerIndex}-attr`);
  
      if (!isEnabled) {
        if (idInput) {
          idInput.value = '';
          idInput.disabled = true;
        }
        if (attrInput) attrInput.disabled = true;
        continue;
      }
  
      const variants = getVariants(picker);
  
      maxPotentialAddons += getMaxPriceForPicker(variants);
      maxPotentialCompareAddons += getMaxCompareForPicker(variants);
  
      const requiredKeys = getRequiredOptionKeys(variants);
      const selectedByKey = {};
      for (const key of requiredKeys) {
        const n = key.replace('option', '');
        const inputName = `opt${n}-${pickerIndex}`;
        const val = getSelectedValue(picker, inputName);
        if (val) selectedByKey[key] = val;
      }
  
      const matchedVariant = findMatchingVariant(variants, selectedByKey);
  
      if (matchedVariant && matchedVariant.id) {
        if (idInput) {
          idInput.value = String(matchedVariant.id);
        }
        if (idInput) idInput.disabled = false;
        if (attrInput) attrInput.disabled = false;
        totalCentsFromAddons += toCents(matchedVariant.price);
        totalCompareCentsFromAddons += getCompareAtCents(matchedVariant);
      } else {
        allPickersComplete = false;
        if (idInput) {
          idInput.value = '';
          idInput.disabled = true;
        }
        if (attrInput) attrInput.disabled = true;
      }
    }
  
    return {
      totalCentsFromAddons,
      totalCompareCentsFromAddons,       
      allPickersComplete,
      maxPotentialAddons,
      maxPotentialCompareAddons        
    };
  }

  function refreshAllUI() {
  const {
    totalCentsFromAddons,
    totalCompareCentsFromAddons,
    allPickersComplete,
    maxPotentialAddons,
    maxPotentialCompareAddons
  } = computePickersStateAndApplyHiddenInputs();

  // Base price: prefer currently selected main variant; fallback to initial globals
  const currentMainVariant = getCurrentMainVariant();
  const basePriceCents = currentMainVariant
    ? toCents(currentMainVariant.price)
    : toCents(window.productprice || 0);
  const rawBaseCompare = currentMainVariant
    ? toCents(currentMainVariant.compare_at_price || 0)
    : toCents(window.productcompareprice || 0);
  const baseCompareCents = Math.max(basePriceCents, rawBaseCompare);

  const totalPriceCents = basePriceCents + totalCentsFromAddons;
  const totalCompareCents = baseCompareCents + totalCompareCentsFromAddons;

  const totalPriceElement = queryOne('#total-price');
  const totalCompareElement = queryOne('#total-compare');
  const placeholderPriceElement = queryOne('#placeholder-price');
  const placeholderCompareElement = queryOne('#placeholder-compare');

  const hasDiscount = totalCompareCents > totalPriceCents; // NEW

  if (allPickersComplete) {
    if (totalPriceElement) {
      totalPriceElement.textContent = formatMoney(totalPriceCents);
      totalPriceElement.classList.remove('!tw-hidden');
      totalPriceElement.classList.toggle('discounted', hasDiscount); // NEW
    }
    if (placeholderPriceElement) {
      placeholderPriceElement.classList.add('!tw-hidden');
      placeholderPriceElement.textContent = '';
    }

    if (totalCompareElement) {
      if (hasDiscount) {
        totalCompareElement.textContent = formatMoney(totalCompareCents);
        totalCompareElement.classList.remove('!tw-hidden');
      } else {
        totalCompareElement.classList.add('!tw-hidden');
        totalCompareElement.textContent = '';
      }
    }
    if (placeholderCompareElement) {
      placeholderCompareElement.classList.add('!tw-hidden');
      placeholderCompareElement.textContent = '';
    }
  } else {
    const upperBoundPrice = basePriceCents + maxPotentialAddons;
    const upperBoundCompare = baseCompareCents + maxPotentialCompareAddons;

    const placeholderText =
      maxPotentialAddons > 0
        ? `${formatMoney(basePriceCents)} – ${formatMoney(upperBoundPrice)}`
        : `${formatMoney(basePriceCents)}`;

    if (placeholderPriceElement) {
      placeholderPriceElement.textContent = placeholderText;
      placeholderPriceElement.classList.remove('!tw-hidden');
    }
    if (totalPriceElement) {
      totalPriceElement.classList.add('!tw-hidden');
      totalPriceElement.textContent = '';
      totalPriceElement.classList.remove('discounted');
    }

    if (placeholderCompareElement) {
      const showPlaceholderCompare = baseCompareCents > basePriceCents;
      if (showPlaceholderCompare) {
        placeholderCompareElement.textContent = formatMoney(baseCompareCents);
        placeholderCompareElement.classList.remove('!tw-hidden');
      } else {
        placeholderCompareElement.classList.add('!tw-hidden');
        placeholderCompareElement.textContent = '';
      }
    }
    if (totalCompareElement) {
      totalCompareElement.classList.add('!tw-hidden');
      totalCompareElement.textContent = '';
    }
  }

  const priceBlock = queryOne('.custom_variants_price');
  if (priceBlock) priceBlock.classList.remove('!tw-hidden');

  const anydayInstallmentEls = queryAll('.anyday-installment-price');
  anydayInstallmentEls.forEach((el) => {
    el.textContent = formatMoney(Math.ceil(totalPriceCents / 4));
  });

  const addToCartButton = queryOne('#AddToCart');
  if (addToCartButton) {
    const isSoldOut = !!currentMainVariant && !isVariantAvailable(currentMainVariant);
    const disabled = !allPickersComplete || isSoldOut;
    addToCartButton.disabled = disabled;
    addToCartButton.setAttribute('aria-disabled', String(disabled));
    addToCartButton.classList.toggle('sold-out', isSoldOut);
    const labelA = addToCartButton.querySelector('.single-add-to-cart-button--text-custom');
    const labelB = addToCartButton.querySelector('.single-add-to-cart-button--text');
    const selectOptionsText =
      addToCartButton.getAttribute('data-select-options') ||
      labelA?.textContent ||
      labelB?.textContent ||
      'Vælg tilvalg';
    const addToCartText =
      addToCartButton.getAttribute('data-add-to-cart') ||
      labelA?.textContent ||
      labelB?.textContent ||
      'Læg i kurv';
    const soldOutText =
      addToCartButton.getAttribute('data-sold-out') ||
      addToCartButton.getAttribute('data-soldout') ||
      'Sold out';
    let finalText = addToCartText;
    if (!allPickersComplete) finalText = selectOptionsText;
    if (isSoldOut) finalText = soldOutText;
    if (labelA) labelA.textContent = finalText;
    if (labelB) labelB.textContent = finalText;

    // Swap BIS and button visibility when sold out
    const wrapper = addToCartButton.closest('.product-form__buttons, .add_to_cart_holder, .product-form, .product-form__buttons form') || document;
    const bisEl = wrapper.querySelector('.klaviyo-bis-trigger');
    if (!allPickersComplete) {
      // Show button (prompt selection), hide BIS
      addToCartButton.style.display = '';
      if (bisEl) bisEl.style.display = 'none';
    } else {
      if (isSoldOut) {
        addToCartButton.style.display = 'none';
        if (bisEl) bisEl.style.display = '';
      } else {
        addToCartButton.style.display = '';
        if (bisEl) bisEl.style.display = 'none';
      }
    }
  }
}


  function handleInteractiveEvent(event) {
    const element = event.target;
    if (!(element instanceof Element)) return;

    if (
      element.matches('input[name="variant"], select[name="variant"]') ||
      element.closest('.variant-picker') ||
      element.classList.contains('addon-toggle') ||
      element.closest('option-fieldsets') ||
      element.closest('fieldset[data-index^="option"]')
    ) {
      refreshAllUI();
    }
  }

  async function ajaxAddMultiple(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items })
    });
    if (!res.ok) {
      let msg = 'Kunne ikke lægge i kurv.';
      try {
        const err = await res.json();
        if (err && err.description) msg = err.description;
      } catch {}
      throw new Error(msg);
    }
    return res.json();
  }

  function getMainVariantId(btn) {
    const form =
      btn?.closest('form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]');

    const idField = form?.querySelector('[name="id"]');
    if (idField && idField.value) return Number(idField.value);

    const dataId = btn?.getAttribute('data-variant-id');
    if (dataId) return Number(dataId);

    const currentMainVariant = getCurrentMainVariant();
    if (currentMainVariant && currentMainVariant.id) {
      return Number(currentMainVariant.id);
    }

    try {
      return Number(window.product?.selected_or_first_available_variant?.id);
    } catch {}
    return null;
  }

  function getQuantity() {
    const q = document.querySelector('input[name="quantity"], #Quantity');
    const n = Number(q?.value || 1);
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  function buildItemsPayload(btn, groupId) {
  const { allPickersComplete } = computePickersStateAndApplyHiddenInputs();

  const mainId = getMainVariantId(btn);
  const qty = getQuantity();

  if (!mainId) throw new Error('Variant kunne ikke findes.');
  if (!allPickersComplete) throw new Error('Vælg tilvalg først.');

  const preOrderText = (btn?.getAttribute('data-pre-order-text') || '').trim();
  const preOrderDate = (btn?.getAttribute('data-pre-order-date') || '').trim();

  const mainProperties = { _uniq_id: groupId };
  if (preOrderText && preOrderDate) {
    mainProperties[preOrderText] = preOrderDate;
  }

  const items = [
    {
      id: mainId,
      quantity: qty,
      properties: mainProperties
    }
  ];

  const addonIdInputs = Array.from(
    document.querySelectorAll('input[id^="addon-"][id$="-id"]')
  );

  addonIdInputs.forEach((idInput) => {
    if (idInput.disabled) return;
    const val = Number(idInput.value);
    if (!val) return;

    items.push({
      id: val,
      quantity: qty,
      parent_id: mainId,
      properties: { _tilkøb_id: groupId }
    });
  });

  return items;
}

  async function onAddToCartClick(e, delegatedBtn) {
    const btn = delegatedBtn;
    if (!btn || btn.disabled) return;

    // Block when current main variant is unavailable
    const v = getCurrentMainVariant();
    if (v && !isVariantAvailable(v)) {
      const soldOutText =
        btn.getAttribute('data-sold-out') ||
        btn.getAttribute('data-soldout') ||
        'Sold out';
      alert(soldOutText);
      return;
    }

    const label = btn.querySelector('.single-add-to-cart-button--text-custom');
    const label2 = btn.querySelector('.single-add-to-cart-button--text');
    const prevText = label?.textContent;
    const prevText2 = label2?.textContent;

    const groupId = newGroupId();

    try {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      const adding = btn.getAttribute('data-adding') || 'Lægger i kurv…';
      if (label) label.textContent = adding;
      if (label2) label2.textContent = adding;

      const items = buildItemsPayload(btn, groupId);
      await ajaxAddMultiple(items);

      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.querySelector('cart-drawer')?.open();
    } catch (err) {
      alert(err?.message || 'Noget gik galt.');
    } finally {
      btn.disabled = false;
      btn.setAttribute('aria-disabled', 'false');
      if (label) label.textContent = prevText || 'Læg i kurv';
      if (label2) label2.textContent = prevText2 || 'Læg i kurv';
      refreshAllUI();
    }
  }

  function initialize() {
    refreshAllUI();

    document.addEventListener('click', handleInteractiveEvent, true);
    document.addEventListener('change', handleInteractiveEvent, true);
    document.addEventListener('input', handleInteractiveEvent, true);
    document.addEventListener('product:variant-change', refreshAllUI, true);

    document.addEventListener(
      'click',
      (e) => {
        const btn = e.target instanceof Element ? e.target.closest('#AddToCart') : null;
        if (!btn) return;
        onAddToCartClick(e, btn);
      },
      true
    );

    document.addEventListener('shopify:section:load', refreshAllUI);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }
})();
